import { useMemo, useState } from 'react'
import { IProject, IProjectSlot, ProjectSlotRole, ProjectToSlot } from 'screens/Projects/projects.types'
import TeamsTable from 'screens/Teams/components/TeamsTable'
import Button from 'shared/Buttons/Button/Button'
import TextField from 'shared/Forms/TextField'
import SlideSelect from 'shared/Inputs/SlideSelect'
import TextInput from 'shared/Inputs/TextInput'
import Modal from 'shared/Modal'
import ModalHeader from 'shared/Modal/ModalHeader'
import Sheet from 'shared/Sheet'
import ProjectSlotRoles from '../ProjectSlotRoles'
import projectSlotApi from 'screens/Projects/projectSlot.api'
import useHttpLoader from 'shared/hooks/useHttpLoader'
import { ITeam } from 'screens/Teams/teams.types'
import Chip from 'shared/Chip'

interface IProps {
  slot?: ProjectToSlot
  projectId: IProject['id']
  onClose: () => void
  onCreate: (slot: ProjectToSlot) => void
  onChange: (slot: ProjectToSlot) => void
  onDelete: () => void
}

const defaultSlot = {
  title: '',
  description: '',
  teamId: null,
}

const ProjectSlotSettingsModal = (props: IProps) => {
  const [slotDraft, setSlotDraft] = useState(props.slot ?? defaultSlot)
  const [selectedSlide, setSelectedSlide] = useState(props.slot ? 'slot-team' : 'create-slot')
  const slidesOptions = useMemo(() => {
    if (!props.slot) {
      return [{ value: 'create-slot', title: 'Создать слот' }]
    }

    return [
      { value: 'slot-team', title: `Команда` },
      { value: 'slot', title: 'Слот' },
      { value: 'slot-roles', title: 'Роли' },
    ]
  }, [props.slot])

  const { wait } = useHttpLoader()

  const updateSlot = (slot: Partial<IProjectSlot>) => {
    if (!props.slot?.id) return
    wait(projectSlotApi.updateProjectSlot(props.slot.id, slot), (resp) => {
      if (resp.status === 'success') {
        props.onChange(resp.body)
      }
    })
  }

  const createSlot = () => {
    wait(projectSlotApi.createProjectSlot({ ...slotDraft, projectId: props.projectId }), (resp) => {
      if (resp.status === 'success') {
        props.onCreate(resp.body)
      }
    })
  }

  const handleSave = () => {
    if (props.slot) {
      updateSlot(slotDraft)
      return
    }
    createSlot()
  }

  const deleteSlot = () => {
    if (!props.slot) return
    wait(projectSlotApi.deleteProjectSlot(props.slot.id), (resp) => {
      if (resp.status === 'success') {
        props.onDelete()
      }
    })
  }

  const setTeam = (team: ITeam) => {
    updateSlot({ teamId: team.id })
  }

  const handleChange = (val: string, name: string) => {
    setSlotDraft({ ...slotDraft, [name]: val })
  }

  const handleChangeRoles = (roles: ProjectSlotRole[]) => {
    if (!props.slot) return
    const slot: IProjectSlot = {
      ...props.slot,
      roles,
    }
    props.onChange(slot)
  }

  const handleClearTeam = () => {
    updateSlot({ teamId: null })
  }

  const isValid = !!slotDraft.title

  const renderSlot = () => {
    return (
      <div className='flex-lines gap24'>
        <Sheet>
          <div className='flex-lines'>
            <TextField header='Наименование'>
              <TextInput value={slotDraft.title} name='title' onChange={handleChange} placeholder='Слот 1' />
            </TextField>
            <TextField header='Описание'>
              <TextInput
                value={slotDraft.description}
                name='description'
                onChange={handleChange}
                placeholder='Слот для команды'
              />
            </TextField>
            <div className='form-footer'>
              <div className='inline-flex'>
                <Button theme='primary' style={{ width: 'fit-content' }} disabled={!isValid} onClick={handleSave}>
                  {!!props.slot ? 'Обновить' : 'Создать'}
                </Button>
                {!!props.slot && (
                  <Button theme='danger' style={{ width: 'fit-content' }} onClick={deleteSlot}>
                    Удалить
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Sheet>
      </div>
    )
  }

  const renderTeamSelect = () => {
    return (
      <div className='inline-flex' style={{ height: '100%' }}>
        {props.slot?.claimedBy && (
          <Chip
            label={props.slot?.claimedBy?.name}
            style={{ width: '236px' }}
            onDelete={handleClearTeam}
            variant={'active'}
          />
        )}
        <TeamsTable onClick={setTeam} />
      </div>
    )
  }

  const renderContent = () => {
    switch (selectedSlide) {
      case 'slot-team':
        return renderTeamSelect()
      case 'create-slot':
      case 'slot':
        return renderSlot()
      case 'slot-roles':
        return (
          props.slot && (
            <ProjectSlotRoles
              onChange={handleChangeRoles}
              slotId={props.slot?.id}
              slotRoles={props.slot?.roles ?? []}
            />
          )
        )
      default:
        return null
    }
  }

  return (
    <Modal onClose={props.onClose} size='hg' shouldAnimate={false}>
      <ModalHeader onClose={props.onClose}>Слот: {props.slot?.title}</ModalHeader>
      <div className='modal-content flex-lines' style={{ height: '60vh' }}>
        <SlideSelect options={slidesOptions} selectedOption={selectedSlide} onSelect={(val) => setSelectedSlide(val)} />
        {renderContent()}
      </div>
    </Modal>
  )
}

export default ProjectSlotSettingsModal
